import { Typography, alpha, Badge, Divider, Button } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { MY_MEETING } from 'routes/constants'
import InCardStack from 'components/atoms/container/InCardStack'
import RowStack from 'components/atoms/container/RowStack'
import BoxWithBackground from 'components/atoms/container/BoxWithBackground'
import ProfileAvatar from 'components/atoms/ProfileAvatar'
import colors from 'styles/colors'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CardCarousel from 'components/molecules/CardCarousel'
import logoAvatar from 'assets/images/logoAvatar.svg'
import { IconButtonWithTooltip } from 'components/atoms/button/StyledButton'
import { ReactComponent as NoteTakerIcon } from 'assets/images/noteTaker.svg'
import { eventStarted, eventEnded } from 'utils/eventUtils'
import { trackMeetingJoined } from 'services/amplitudeMeetingTracking'

export const RenderUpcomingButtons = ({ event }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const eventHasStarted = eventStarted(event)
  const eventHasEnded = eventEnded(event)
  const handleJoinMeeting = () => {
    trackMeetingJoined(event, 'Meeting list')
    window.open(event.meetingUrl, '_blank')
  }
  return (
    <RowStack spacing={2} width='unset'>
      <Button
        variant='outlined'
        color='primary'
        size='small'
        onClick={() => {
          navigate(MY_MEETING, {
            state: {
              userCalendarEventId: event?.id
            }
          })
        }}>
        {eventHasEnded
          ? t('myTeam.review')
          : eventHasStarted
            ? t('stepNote.takeNotes')
            : t('myTeam.prepare')}
      </Button>

      {event.meetingUrl && eventHasStarted && !eventHasEnded && (
        <Button size='small' onClick={handleJoinMeeting}>
          {t('myTeam.join')}
        </Button>
      )}
    </RowStack>
  )
}

const UpcomingOneOnOnes = ({ events, paginateEvents }) => {
  const { t, i18n } = useTranslation()
  moment.locale(i18n.resolvedLanguage)

  const UserAvatar = ({ user }) => {
    return (
      <Badge
        overlap='circular'
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        badgeContent={
          <CheckCircleIcon sx={{ color: colors.SuccessGreen, fontSize: 16 }} />
        }>
        <ProfileAvatar user={user} size={44} />
      </Badge>
    )
  }

  const RenderEvent = ({ event }) => {
    return (
      <BoxWithBackground
        sx={{
          width: 328,
          padding: 8,
          borderRadius: 3,
          backgroundColor: colors.White
        }}>
        <InCardStack spacing={12}>
          <InCardStack spacing={2} width={'100%'} alignItems={'flex-start'}>
            <RowStack justifyContent={'space-between'}>
              <Typography variant='h3' width={'100%'} noWrap>
                {event?.subject}
              </Typography>
              {event?.botId && (
                <IconButtonWithTooltip
                  title={t('myTeam.noteTakerAddedToMeeting')}
                  icon={<NoteTakerIcon />}
                  iconContainerProps={{
                    size: 24
                  }}
                />
              )}
            </RowStack>
            <Typography variant='body1Small'>
              {moment(event.startTime).format('h:mma')} -{' '}
              {moment(event.endTime).format('h:mma')}
            </Typography>
          </InCardStack>
          <RowStack spacing={0} justifyContent={'space-between'}>
            <RowStack spacing={2} flex={1}>
              <UserAvatar
                user={
                  event?.userConnectionEvent?.userConnection?.connection ||
                  event?.userConnectionEvent?.userConnection
                }
              />
              <UserAvatar
                user={event?.userConnectionEvent?.userConnection?.connectedBy}
              />
              {event?.botId && (
                <UserAvatar user={{ profileImage: logoAvatar }} />
              )}
            </RowStack>
            <RenderUpcomingButtons event={event} />
          </RowStack>
        </InCardStack>
      </BoxWithBackground>
    )
  }

  const RenderEventDay = ({ day, setWeekDivider }) => {
    const isToday = moment(day).isSame(moment(), 'day')

    return (
      <RowStack spacing={0}>
        <BoxWithBackground
          sx={{
            borderRadius: 3,
            backgroundColor: isToday
              ? alpha(colors.SuccessGreen, 0.1)
              : colors.MetalGrey50
          }}>
          <InCardStack spacing={2} alignItems={'flex-start'}>
            <Typography
              variant='eyebrow'
              color={isToday ? colors.SuccessGreen : colors.MidnightTeal400}>
              {isToday && `${t('myTeam.today')} - `}
              {moment(day).format('dddd, MMMM D')}
            </Typography>

            <RowStack spacing={4}>
              {events[day].map((event, index) => (
                <RenderEvent key={`event-${index}`} event={event} />
              ))}
            </RowStack>
          </InCardStack>
        </BoxWithBackground>

        {setWeekDivider && (
          <Divider
            orientation='vertical'
            sx={{
              height: 152,
              width: '1px',
              marginLeft: 9,
              marginRight: 6,
              borderColor: colors.Grey200
            }}
          />
        )}
      </RowStack>
    )
  }

  return (
    <>
      <CardCarousel
        customWidth={'auto'}
        containerSx={{
          width: '100%',
          height: 'auto'
        }}
        slideStyle={{
          paddingLeft: 0
        }}
        paddingX={6}
        showNavigation={false}
        onEndReached={() => paginateEvents()}
        cards={Object.keys(events).map((day, index) => {
          // check if a new week is starting anD next event exists on new weeks first day and add a divider
          const daysArray = Object.keys(events).sort(
            (a, b) => moment(a) - moment(b)
          ) // Ensure sorted order
          const nextDay = daysArray[index + 1] // Get the next day, if it exists
          const isLastEventInWeek =
            moment(day).isoWeek() !== moment(nextDay).isoWeek()
          const setWeekDivider = nextDay && isLastEventInWeek
          return (
            <RenderEventDay
              key={`day-${index}`}
              day={day}
              setWeekDivider={setWeekDivider}
            />
          )
        })}
      />
    </>
  )
}

export default UpcomingOneOnOnes
